import React, { useEffect, useState } from "react";
import {
  Box,
  ButtonStyled,
  FileUploaderStyled,
  FormButtonStyled,
  HeadingStyled,
  LabelStyled,
  SpanStyled,
  TextStyled,
  ThemeLoader,
} from "../../../../components/styled/Elements";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import ValidatedInput from "../../../../components/common/inputFields/ValidatedInput";
import { Col, Form, Row } from "react-bootstrap";
import InformationList from "../../../../components/common/informationList/InformationList";
import InformationListItem from "../../../../components/common/informationList/InformationListItem";
import {
  Cancel,
  FileIcon,
  FileUpload,
  RoundedArrow,
  TrashIcon,
} from "../../../../components/styled/AllImages";
import ValidationController from "../../../../components/common/inputFields/ValidationController";
import AuthApi from "../../../../services/api/authApi";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import InvoiceDocument from "../../../../components/common/invoiceDoc/InvoiceDocument";
import { pdf } from "@react-pdf/renderer";
import { formateAmounts } from "../../../../utils";

const SignupStep3 = ({
  handleSteps,
  paymentMethod,
  bankInfo,
  planInfo,
  addLicenseFrm,
  register,
  control,
  getFieldState,
  errors,
  watch,
  loading,
  totalAmount,
  user,
  validateCurrentStep,
  promoDiscount,
}) => {
  const TAP_KEY = process.env.REACT_APP_TAP_PUBLIC_KEY;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [tapLoading, setTapLoading] = useState(true);
  const [previewImg, setPreviewImg] = useState(null);
  const [invoiceNum, setInvoiceNum] = useState("");

  // Watch for file changes
  const receiptFile = watch("payment_proof");
  // const numOfLicenses = watch("number_of_licenses");
  const promoCode = watch("promotion_code");

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImg(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    setPreviewImg(null);
  };

  const handleInvoiceView = async () => {
    const invoiceData = {
      totalAmount: formateAmounts(totalAmount),
      bankName: bankInfo?.bank_detail?.bank_name,
      iban: bankInfo?.bank_detail?.iban,
      beneficiaryName: bankInfo?.bank_detail?.account_title,
      userName: user?.name,
      // numOfLicenses,
      planInfo,
      invoiceNum,
    };
    const blob = await pdf(<InvoiceDocument data={invoiceData} />).toBlob();
    const blobUrl = URL.createObjectURL(blob);

    // Open in new tab
    window.open(blobUrl, "_blank");

    // Trigger download
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = "invoice.pdf";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const getInvoiceNumber = async () => {
    try {
      const { data, code } = await AuthApi.getInvoiceNumber();
      if (code === 200) {
        setInvoiceNum(data);
      }
    } catch (error) {}
  };

  useEffect(() => {
    getInvoiceNumber();
  }, []);

  // ************* Tap payment gateway configuration ************ //
  useEffect(() => {
    if (paymentMethod === "card") {
      // Ensure SDK is loaded
      if (!window.CardSDK) {
        console.error("Tap Card SDK not found");
        return;
      }

      const { renderTapCard, Theme, Currencies, Direction, Edges, Locale } =
        window.CardSDK;

      // Render the card UI
      const { unmount } = renderTapCard("tap-card-element", {
        publicKey: TAP_KEY,
        merchant: {
          id: "62682411",
        },
        transaction: {
          amount: totalAmount,
          currency: Currencies.SAR,
        },
        customer: {
          id: user?.tap_customer_id, //Tap's customer ID with syntax cus_xxx
          name: [
            {
              lang: Locale.EN,
              first: user?.name,
              last: "",
              middle: "",
            },
          ],
          contact: {
            email: user?.email,
          },
        },
        acceptance: {
          supportedBrands: ["VISA", "MASTERCARD", "AMERICAN_EXPRESS", "MADA"],
          supportedCards: "ALL",
        },
        fields: {
          cardHolder: true,
        },
        addons: {
          displayPaymentBrands: true,
          loader: true,
          saveCard: true,
        },
        interface: {
          locale: Locale.EN,
          theme: Theme.LIGHT,
          edges: Edges.CURVED,
          direction: Direction.LTR,
        },
        onSuccess: (data) => {
          // console.log("onSuccess", data);
          signUpStepTwo(data?.id);
        },
        onReady: () => {
          console.log("SDK Ready");
          setTapLoading(false);
        },
        onFocus: () => console.log("Input Focused"),
        onBinIdentification: (data) => console.log("onBinIdentification", data),
        onValidInput: (data) => console.log("onValidInputChange", data),
        onInvalidInput: (data) => console.log("onInvalidInput", data),
        onError: (data) => {
          console.log("onError", data);
          toast.error(data?.message);
        },
        onChangeSaveCardLater: (isSaveCardSelected) =>
          console.log(isSaveCardSelected, " :onChangeSaveCardLater"),
      });

      // Clean up
      return () => {
        unmount();
      };
    }
  }, []);

  const finalSignup = async (formObj) => {
    const formData = new FormData();
    // for (let key in formObj) {
    //   formData.append(key, formData[key]);
    // }
    Object.entries(formObj).forEach(([key, value]) => {
      formData.append(key, value);
    });

    try {
      const { code, message, data } = await AuthApi.signupStepTwo(formData);
      if (code === 200) {
        if (paymentMethod === "card") {
          window.open(data?.payment_url, "_self");
        } else {
          toast.success(message);
          navigate("/login");
        }
      }
      if (code === 100 || code === 204) {
        toast.error(message);
      }
    } catch (error) {
    } finally {
      setSubmitLoading(false);
    }
  };

  const signUpStepTwo = async (token = "") => {
    const isCurrentStepValid = await validateCurrentStep();
    const baseCost = parseFloat(planInfo?.amount);
    const discount = baseCost * (parseFloat(promoDiscount) / 100);
    if (isCurrentStepValid) {
      setSubmitLoading(true);
      const baseObj = {
        user_id: user?.id,
        plan_id: planInfo?.id,
        plan_duration : planInfo?.duration || 1,
        amount: totalAmount,
        payment_method: paymentMethod,
        // number_of_licenses: numOfLicenses,
        sub_total: baseCost,
        ...(bankInfo?.vat && {
          vat_percentage: bankInfo?.vat,
          vat_value:
            (parseFloat(bankInfo?.vat) / 100) * (baseCost - (discount || 0)),
        }),
        ...(promoCode && {
          promotion_code: promoCode,
          discount_percentage: promoDiscount,
          discount_value: discount,
        }),
      };
      const paymentSpecifics =
        paymentMethod === "card"
          ? { authorize_token_id: token }
          : {
              invoice_number: invoiceNum,
              payment_proof: receiptFile,
            };
      finalSignup({
        ...baseObj,
        ...paymentSpecifics,
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const { tokenize, saveCard } = window.CardSDK;
      await tokenize();
      await saveCard();
      // You can now send tokenResult.id to your backend for payment processing
      // Example:
      // await fetch('/api/charge', {
      //   method: 'POST',
      //   body: JSON.stringify({ token: tokenResult.id }),
      //   headers: { 'Content-Type': 'application/json' }
      // });
    } catch (err) {
      console.error("Tokenization failed:", err);
    }
  };

  return (
    <Step3Wrapper>
      <Box>
        {paymentMethod === "card" ? (
          <>
            {tapLoading ? (
              <ThemeLoader />
            ) : (
              <Box className="mb-4">
                <HeadingStyled size="24px" weight="bold" color="var(--text)">
                  {t("pay_through_card")}
                </HeadingStyled>
                <TextStyled size="16px" color="var(--text2)">
                  {t("please_enter_card_info_bellow")}
                </TextStyled>
              </Box>
            )}
            <div id="tap-card-element"></div>
            {/* <Box id="checkout-container"></Box> */}
            {/* <Box>
              <ValidatedInput
                name={"card_holder_name"}
                label={"card_holder_name"}
                placeholder={"chNamePlaceholder"}
                type="input"
                fieldType="text"
                className={"mb-3 combined flex-wrap p-0"}
                bg="var(--white)"
                register={register}
              />
              <ValidatedInput
                name={"card_number"}
                label={"card_number"}
                placeholder={"card_number"}
                type="input"
                fieldType="text"
                className={"mb-3 combined flex-wrap p-0"}
                bg="var(--white)"
                register={register}
              />
              <Row>
                <Col lg={8}>
                  <ValidatedInput
                    name={"expiry_date"}
                    label={"expiry_date"}
                    placeholder={"expiry_date"}
                    type="input"
                    fieldType="date"
                    className={"mb-3 combined flex-wrap p-0"}
                    bg="var(--white)"
                    register={register}
                  />
                </Col>
                <Col lg={4}>
                  <ValidatedInput
                    name={"cvv"}
                    label={"cvv"}
                    placeholder={"cvv"}
                    type="input"
                    fieldType="number"
                    className={"mb-3 combined flex-wrap p-0"}
                    bg="var(--white)"
                    register={register}
                    classNameLabel="w-auto"
                  />
                </Col>
              </Row>
            </Box> */}
          </>
        ) : (
          <>
            <Box className="mb-4">
              <HeadingStyled size="24px" weight="bold" color="var(--text)">
                {t("bank_account_information")}
              </HeadingStyled>
              <TextStyled size="16px" color="var(--text2)">
                {t("please_see_account_info_bellow")}
              </TextStyled>
            </Box>
            <InformationList>
              <InformationListItem
                label={t("total_amount")}
                value={`${formateAmounts(totalAmount)} ${t("usd")}`}
              />
              <InformationListItem
                label={t("bank_name")}
                value={bankInfo?.bank_detail?.bank_name}
              />
              <InformationListItem
                label={t("iban")}
                value={bankInfo?.bank_detail?.iban}
              />
              <InformationListItem
                label={t("beneficiary_name")}
                value={bankInfo?.bank_detail?.account_title}
              />
              <Box className="d-flex justify-content-center py-3">
                <SpanStyled
                  size="16px"
                  color="var(--themeColor)"
                  className="cursor-pointer"
                  onClick={handleInvoiceView}
                >
                  <FileIcon />
                  &nbsp;
                  {t("see_invoice")}
                </SpanStyled>
              </Box>
            </InformationList>
            <Box className="mt-3">
              <LabelStyled size="16px" color="var(--text)" weight="700">
                {t("upload_receipt")}
              </LabelStyled>
              <ValidationController
                name={"payment_proof"}
                validationConfig={{
                  required: true,
                }}
                errors={errors}
                control={control}
                renderer={({ field }) => (
                  <FileUploaderStyled
                    className="d-flex align-items-center justify-content-center w-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Form
                      as="input"
                      name="payment_proof"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        handleFileChange(e);
                        field.onChange(e.target.files[0]);
                      }}
                      ref={field.ref}
                      className="d-none"
                    />
                    {previewImg ? (
                      // <Box
                      //   className="d-flex align-items-center justify-content-between gap-2 w-100 mt-n4 preview-box"
                      //   // onClick={(e) => e.stopPropagation()}
                      // >
                      //   <embed
                      //     src={previewImg}
                      //     className="attachment w-100 h-100"
                      //     type="application/pdf"
                      //   />
                      //   <Box className="d-flex align-items-center flex-column gap-1">
                      //     <span
                      //       className="default-icon replace-icon"
                      //       // onClick={handleReplaceFile}
                      //     >
                      //       <RoundedArrow />
                      //     </span>
                      //     <span
                      //       className="default-icon delete-icon"
                      //       onClick={handleRemoveFile}
                      //     >
                      //       <TrashIcon />
                      //     </span>
                      //   </Box>
                      // </Box>
                      <Box className="d-flex align-items-center justify-content-between gap-2 w-100 mt-n4 preview-box">
                        {/* Scrollable image box */}
                        <Box
                          style={{ overflow: "auto", height: "100%" }}
                          className="flex-grow-1"
                        >
                          <img
                            src={previewImg}
                            className="attachment w-100"
                            alt="File preview"
                            style={{ objectFit: "contain" }}
                          />
                        </Box>

                        {/* Icons (fixed, not scrollable) */}
                        <Box className="d-flex align-items-center flex-column gap-1">
                          <span
                            className="default-icon replace-icon"
                            // onClick={handleReplaceFile}
                          >
                            <RoundedArrow />
                          </span>
                          <span
                            className="default-icon delete-icon"
                            onClick={handleRemoveFile}
                          >
                            <TrashIcon />
                          </span>
                        </Box>
                      </Box>
                    ) : (
                      <>
                        <Box className="d-flex align-items-center justify-content-center mt-n4 w-100">
                          <FileUpload size={26} />
                          &nbsp;&nbsp;
                          <SpanStyled color="var(--text)" size="14px">
                            {t("upload_receipt")}
                          </SpanStyled>
                        </Box>
                      </>
                    )}
                  </FileUploaderStyled>
                )}
              />
            </Box>
          </>
        )}
      </Box>
      {/* <FormButtonStyled
        label={t("make_payment")}
        color="var(--white)"
        bg="var(--themeColor)"
        className="w-100"
        clName="w-100 my-4"
        type="button"
      /> */}
      <FormButtonStyled
        label={addLicenseFrm ? t("Submit") : t("sign_up")}
        color="var(--white)"
        bg="var(--themeColor)"
        className="w-100"
        clName="w-100 my-4"
        isLoading={submitLoading}
        disabled={submitLoading}
        onClick={() =>
          paymentMethod === "card" ? handleSubmit() : signUpStepTwo()
        }
        type="button"
      />
    </Step3Wrapper>
  );
};

export default SignupStep3;

const Step3Wrapper = styled.div`
  #card-footer {
    display: none;
  }
`;
